import * as option from "./configurator_options";
import { LineItem } from "../order/order";
import { ProductType } from "../products/product";

/**
 * FUPOF-191 — shared display helpers for a custom umbrella's canopy configuration.
 *
 * These were originally private methods on Cart.vue's "Details" tooltip. They are
 * now the single source of truth shared by that tooltip and by
 * `buildLineDescription` (the plain-text configuration written to NetSuite's
 * Production Notes), so the two never drift apart.
 */

/**
 * The umbrella Details view assumes each canopy fabric is a swatch object
 * ({ name, mfr_code, _id }). NetSuite-generated and NetSuite-added lines come
 * back with a SKU-parsed configuration whose fabrics are bare code strings
 * (e.g. "R-172"), which have no name/mfr_code to render. Only treat the canopy
 * as structured when it has at least one fabric and every fabric is a real object.
 */
export function hasStructuredCanopy(item: LineItem): boolean {
  const canopy = (item.item_details as any)?.canopy;
  if (!canopy || !Array.isArray(canopy.mainFabric)) return false;
  return (
    canopy.mainFabric.length > 0 &&
    canopy.mainFabric.every((f: any) => f && typeof f === "object")
  );
}

/**
 * The base Details view reads a nested block — `{ base: <BaseModel>, baseFinish:
 * { handle }, baseStem, baseOptions }`. A NetSuite line the SKU parser rebuilt
 * carries the flat ConfigFile form instead: the block IS item_details, its
 * `base` is a bare model code ("36G-SQ") and its `baseFinish` a bare handle
 * ("gs"). Every field the tooltip looks for is then undefined, which is why it
 * opened as an empty box rather than not opening at all.
 */
export function hasStructuredBase(item: LineItem): boolean {
  const base = (item.item_details as any)?.base;
  if (!base || typeof base !== "object") return false;
  return !!base.baseFinish && typeof base.baseFinish === "object";
}

/**
 * The chair Details view renders fabric swatch objects ({ name, mfr_code }).
 * A SKU-parsed chair line carries bare mfr_code strings, so the tooltip would
 * render "Main Fabric : ()" — present but meaningless.
 *
 * Only `mainFabric` is tested. `bindingFabric` is legitimately `null` on a
 * chair ordered without binding, and requiring it here made every such
 * app-authored chair fail the check — which read downstream as "this line's
 * configuration came from NetSuite" and put an "Added in NetSuite" pill on it
 * (see Cart.isReconstructedFromSku). The tooltip guards the binding row
 * itself rather than the whole panel.
 */
export function hasStructuredChair(item: LineItem): boolean {
  const main = (item.item_details as any)?.mainFabric;
  return !!main && typeof main === "object";
}

/**
 * Whether a line has a configuration this app can actually render. Accessories
 * carry none by design and so are never "missing" one; the three configurable
 * types each have their own structural check above.
 */
export function hasStructuredConfig(item: LineItem): boolean {
  switch (item.product_type) {
    case ProductType.Umbrella:
      return hasStructuredCanopy(item);
    case ProductType.Base:
      return hasStructuredBase(item);
    case ProductType.Chair:
      return hasStructuredChair(item);
    default:
      return false;
  }
}

/** Main fabric === Top vent fabric (single fabric each). */
export function hasEqualMainTopFabrics(canopy: any): boolean {
  return canopy.mainFabric.length === 1 &&
    canopy.ventTopFabric.length === 1 &&
    canopy.mainFabric[0]._id === canopy.ventTopFabric[0]._id
    ? true
    : false;
}

/** Main fabric(s) === Middle vent fabric(s). */
export function hasEqualMainMiddleFabrics(canopy: any): boolean {
  const mainFabric = canopy.mainFabric;
  const ventMiddleFabric = canopy.ventMiddleFabric;
  let result = true;
  if (mainFabric.length === ventMiddleFabric.length) {
    for (const index in mainFabric) {
      if (mainFabric[index]._id === ventMiddleFabric[index]._id) {
        continue;
      } else {
        result = false;
        break;
      }
    }
  } else {
    result = false;
  }
  return result;
}

/** Top vent fabric(s) === Middle vent fabric(s). */
export function hasEqualTopMiddleFabrics(canopy: any): boolean {
  const ventTopFabric = canopy.ventTopFabric;
  const ventMiddleFabric = canopy.ventMiddleFabric;
  let result = true;
  if (ventTopFabric.length === ventMiddleFabric.length) {
    for (const index in ventTopFabric) {
      if (ventTopFabric[index]._id === ventMiddleFabric[index]._id) {
        continue;
      } else {
        result = false;
        break;
      }
    }
  } else {
    result = false;
  }
  return result;
}

/** A designer layout is one of the trim presets (D1–D3). */
export function hasDesignerLayout(preset: option.Preset | null): boolean {
  switch (preset) {
    case option.Preset.D1:
    case option.Preset.D2:
    case option.Preset.D3:
      return true;
    default:
      return false;
  }
}

/** Human name for a designer trim preset. */
export function getDesignerLayoutName(preset: option.Preset): string {
  switch (preset) {
    case option.Preset.D1:
      return option.PresetDesignerNames.D1;
    case option.Preset.D2:
      return option.PresetDesignerNames.D2;
    case option.Preset.D3:
      return option.PresetDesignerNames.D3;
    default:
      return option.PresetDesignerNames.None;
  }
}
